import { CatalogApi } from '@backstage/catalog-client';
import {
  DEFAULT_NAMESPACE,
  Entity,
  RELATION_HAS_MEMBER,
  RELATION_HAS_PART,
  RELATION_MEMBER_OF,
  RELATION_PART_OF,
  getCompoundEntityRef,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  OnboardingCourse,
  OnboardingMember,
  OnboardingTeam,
  OnboardingUnit,
} from '../types';

/** Catalog kind used for reusable learning materials. */
export const LEARNING_CONTENT_KIND = 'LearningContent';

/** `spec.type` marking a LearningContent entity as a theme (the parent). */
export const ONBOARDING_THEME_TYPE = 'theme';

/** `spec.type` marking a LearningContent entity as a course (the child). */
export const ONBOARDING_COURSE_TYPE = 'course';

/**
 * `spec.type` marking a LearningContent entity as a unit (a single atomic
 * Tip/Lesson that belongs to a course).
 */
export const ONBOARDING_UNIT_TYPE = 'unit';

/** `spec` field listing the participant groups of a theme. */
export const PARTICIPANT_GROUPS_SPEC_FIELD = 'participantGroups';

/**
 * Normalizes an entity reference so that references originating from different
 * sources (catalog relations, `spec` fields, facts) can be compared safely.
 */
export function normalizeEntityRef(
  entityRef: string,
  defaultKind?: string,
  defaultNamespace = DEFAULT_NAMESPACE,
): string {
  return stringifyEntityRef(
    parseEntityRef(entityRef, {
      defaultKind,
      defaultNamespace,
    }),
  ).toLowerCase();
}

/**
 * Returns the normalized target references of the given standard relation.
 *
 * @param entity - the entity whose relations are inspected.
 * @param relationType - a standard relation type such as `hasPart`.
 * @param options - optionally restricts the results to a single target kind.
 */
export function getRelationTargetRefs(
  entity: Entity,
  relationType: string,
  options?: { kind?: string },
): string[] {
  const kind = options?.kind?.toLowerCase();
  const refs = new Set<string>();

  for (const relation of entity.relations ?? []) {
    if (relation.type !== relationType) {
      continue;
    }

    let normalized: string;
    try {
      normalized = normalizeEntityRef(relation.targetRef);
    } catch {
      // Ignore malformed relations rather than breaking the whole page.
      continue;
    }

    if (kind && !normalized.startsWith(`${kind}:`)) {
      continue;
    }
    refs.add(normalized);
  }

  return [...refs];
}

function isOnboardingCourseEntity(entity: Entity): boolean {
  return (
    entity.kind.toLowerCase() === LEARNING_CONTENT_KIND.toLowerCase() &&
    entity.spec?.type === ONBOARDING_COURSE_TYPE
  );
}

function isOnboardingUnitEntity(entity: Entity): boolean {
  return (
    entity.kind.toLowerCase() === LEARNING_CONTENT_KIND.toLowerCase() &&
    entity.spec?.type === ONBOARDING_UNIT_TYPE
  );
}

function isOnboardingThemeEntity(entity: Entity): boolean {
  return (
    entity.kind.toLowerCase() === LEARNING_CONTENT_KIND.toLowerCase() &&
    entity.spec?.type === ONBOARDING_THEME_TYPE
  );
}

function toOnboardingCourse(entity: Entity): OnboardingCourse {
  const compoundRef = getCompoundEntityRef(entity);
  return {
    entityRef: stringifyEntityRef(entity).toLowerCase(),
    compoundRef,
    name: entity.metadata.name,
    title: entity.metadata.title ?? entity.metadata.name,
    description: entity.metadata.description,
    units: [],
  };
}

function toOnboardingUnit(entity: Entity): OnboardingUnit {
  const compoundRef = getCompoundEntityRef(entity);
  return {
    entityRef: stringifyEntityRef(entity).toLowerCase(),
    compoundRef,
    name: entity.metadata.name,
    title: entity.metadata.title ?? entity.metadata.name,
    description: entity.metadata.description,
  };
}

function toOnboardingMember(
  entity: Entity,
  teamsByRef: Map<string, OnboardingTeam>,
  teamRefs: Set<string>,
): OnboardingMember {
  const compoundRef = getCompoundEntityRef(entity);
  const profile = entity.spec?.profile;
  const isProfileObject =
    typeof profile === 'object' && profile !== null && !Array.isArray(profile);
  const profileDisplayName =
    isProfileObject && typeof profile.displayName === 'string'
      ? profile.displayName
      : undefined;
  const profilePicture =
    isProfileObject && typeof profile.picture === 'string'
      ? profile.picture
      : undefined;

  return {
    entityRef: stringifyEntityRef(entity).toLowerCase(),
    compoundRef,
    name: entity.metadata.name,
    displayName:
      profileDisplayName ?? entity.metadata.title ?? entity.metadata.name,
    picture: profilePicture,
    teams: [...teamRefs]
      .map(teamRef => teamsByRef.get(teamRef))
      .filter((team): team is OnboardingTeam => Boolean(team))
      .sort((first, second) => first.title.localeCompare(second.title)),
  };
}

function toOnboardingTeam(entity: Entity): OnboardingTeam | undefined {
  if (entity.kind.toLowerCase() !== 'group') {
    return undefined;
  }

  return {
    entityRef: stringifyEntityRef(entity).toLowerCase(),
    compoundRef: getCompoundEntityRef(entity),
    title: entity.metadata.title ?? entity.metadata.name,
  };
}

function dedupeByEntityRef<TItem extends { entityRef: string }>(
  items: TItem[],
): TItem[] {
  const byRef = new Map<string, TItem>();
  for (const item of items) {
    if (!byRef.has(item.entityRef)) {
      byRef.set(item.entityRef, item);
    }
  }
  return [...byRef.values()];
}

/**
 * Resolves every onboarding theme registered in the catalog, sorted by
 * title.
 *
 * @remarks
 * Used by the top-level "Learn" page, which lists all themes as cards
 * regardless of the signed-in user's participant group membership.
 */
export async function resolveAllThemes(options: {
  catalogApi: CatalogApi;
}): Promise<Entity[]> {
  const { catalogApi } = options;
  const { items } = await catalogApi.getEntities({
    filter: {
      kind: LEARNING_CONTENT_KIND,
      'spec.type': ONBOARDING_THEME_TYPE,
    },
  });

  return items
    .filter(isOnboardingThemeEntity)
    .sort((first, second) =>
      (first.metadata.title ?? first.metadata.name).localeCompare(
        second.metadata.title ?? second.metadata.name,
      ),
    );
}

/**
 * Resolves the onboarding courses that belong to an onboarding theme.
 *
 * @remarks
 *
 * The parent/child hierarchy uses LearningContent's `spec.partOf` field, which
 * the catalog processor materializes into the `hasPart`/`partOf` relation pair. Both
 * directions are queried and merged, because the `hasPart` relation on the
 * theme is only present once the catalog has processed every child, while the
 * `partOf` query immediately reflects newly added courses. Resolving both makes
 * the tab robust against partially processed catalogs and against courses that
 * are registered from a different location than their theme.
 */
export async function resolveThemeCourses(options: {
  catalogApi: CatalogApi;
  themeEntity: Entity;
}): Promise<OnboardingCourse[]> {
  const { catalogApi, themeEntity } = options;
  const themeRef = stringifyEntityRef(themeEntity).toLowerCase();

  const relationRefs = getRelationTargetRefs(themeEntity, RELATION_HAS_PART, {
    kind: LEARNING_CONTENT_KIND,
  });

  const [relatedEntities, childEntities] = await Promise.all([
    relationRefs.length > 0
      ? catalogApi
          .getEntitiesByRefs({ entityRefs: relationRefs })
          .then(response => response.items)
      : Promise.resolve([]),
    catalogApi
      .getEntities({
        filter: {
          kind: LEARNING_CONTENT_KIND,
          'spec.type': ONBOARDING_COURSE_TYPE,
          [`relations.${RELATION_PART_OF}`]: themeRef,
        },
      })
      .then(response => response.items),
  ]);

  const courses = [...relatedEntities, ...childEntities]
    .filter((entity): entity is Entity => Boolean(entity))
    .filter(isOnboardingCourseEntity)
    .map(toOnboardingCourse);

  return dedupeByEntityRef(courses).sort((a, b) =>
    a.title.localeCompare(b.title),
  );
}

/**
 * Resolves the onboarding units (Tips) that belong to an onboarding course.
 *
 * @remarks
 * Mirrors {@link resolveThemeCourses}: both the standard `hasPart` relation
 * on the course and a reverse `partOf` query on units are resolved and
 * merged, so the list is robust against partially processed catalogs and
 * against units registered from a different location than their course.
 */
export async function resolveCourseUnits(options: {
  catalogApi: CatalogApi;
  courseEntity: Entity;
}): Promise<OnboardingUnit[]> {
  const { catalogApi, courseEntity } = options;
  const courseRef = stringifyEntityRef(courseEntity).toLowerCase();

  const relationRefs = getRelationTargetRefs(courseEntity, RELATION_HAS_PART, {
    kind: LEARNING_CONTENT_KIND,
  });

  const [relatedEntities, childEntities] = await Promise.all([
    relationRefs.length > 0
      ? catalogApi
          .getEntitiesByRefs({ entityRefs: relationRefs })
          .then(response => response.items)
      : Promise.resolve([]),
    catalogApi
      .getEntities({
        filter: {
          kind: LEARNING_CONTENT_KIND,
          'spec.type': ONBOARDING_UNIT_TYPE,
          [`relations.${RELATION_PART_OF}`]: courseRef,
        },
      })
      .then(response => response.items),
  ]);

  const units = [...relatedEntities, ...childEntities]
    .filter((entity): entity is Entity => Boolean(entity))
    .filter(isOnboardingUnitEntity)
    .map(toOnboardingUnit);

  return dedupeByEntityRef(units).sort((a, b) =>
    a.title.localeCompare(b.title),
  );
}

/**
 * Resolves the participant-group references declared by a theme.
 */
export function getParticipantGroupRefs(themeEntity: Entity): string[] {
  const groups = themeEntity.spec?.[PARTICIPANT_GROUPS_SPEC_FIELD];
  if (!Array.isArray(groups)) {
    return [];
  }

  const refs = new Set<string>();
  for (const group of groups) {
    if (typeof group !== 'string') {
      continue;
    }
    try {
      refs.add(
        normalizeEntityRef(
          group,
          'group',
          themeEntity.metadata.namespace ?? DEFAULT_NAMESPACE,
        ),
      );
    } catch {
      continue;
    }
  }
  return [...refs];
}

/**
 * Formats a course title with its parent theme when available.
 */
export function formatCourseTitle(
  courseTitle: string,
  themeTitle?: string,
): string {
  return themeTitle ? `（${themeTitle}）${courseTitle}` : courseTitle;
}

/**
 * Resolves the parent onboarding theme of an onboarding course.
 *
 * Both the course's standard `partOf` relation and a reverse `hasPart` query
 * are used because catalog processing order can temporarily leave either side
 * of the relation unavailable.
 */
export async function resolveCourseTheme(options: {
  catalogApi: CatalogApi;
  courseEntity: Entity;
}): Promise<Entity | undefined> {
  return resolveParentLearningContentEntity({
    ...options,
    childEntity: options.courseEntity,
    parentType: ONBOARDING_THEME_TYPE,
    isParentEntity: isOnboardingThemeEntity,
  });
}

/**
 * Resolves the parent onboarding course of an onboarding unit.
 *
 * Both the unit's standard `partOf` relation and a reverse `hasPart` query
 * are used because catalog processing order can temporarily leave either side
 * of the relation unavailable.
 */
export async function resolveUnitCourse(options: {
  catalogApi: CatalogApi;
  unitEntity: Entity;
}): Promise<Entity | undefined> {
  return resolveParentLearningContentEntity({
    catalogApi: options.catalogApi,
    childEntity: options.unitEntity,
    parentType: ONBOARDING_COURSE_TYPE,
    isParentEntity: isOnboardingCourseEntity,
  });
}

/**
 * Shared implementation backing `resolveCourseTheme` and
 * `resolveUnitCourse`: resolves the parent LearningContent entity (of the
 * given `spec.type`) of a child LearningContent entity.
 */
async function resolveParentLearningContentEntity(options: {
  catalogApi: CatalogApi;
  childEntity: Entity;
  parentType: string;
  isParentEntity: (entity: Entity) => boolean;
}): Promise<Entity | undefined> {
  const { catalogApi, childEntity, parentType, isParentEntity } = options;
  const childRef = stringifyEntityRef(childEntity).toLowerCase();
  const parentRefs = getRelationTargetRefs(childEntity, RELATION_PART_OF, {
    kind: LEARNING_CONTENT_KIND,
  });

  const [relatedEntities, parentEntities] = await Promise.all([
    parentRefs.length > 0
      ? catalogApi
          .getEntitiesByRefs({ entityRefs: parentRefs })
          .then(response => response.items)
      : Promise.resolve([]),
    catalogApi
      .getEntities({
        filter: {
          kind: LEARNING_CONTENT_KIND,
          'spec.type': parentType,
          [`relations.${RELATION_HAS_PART}`]: childRef,
        },
      })
      .then(response => response.items),
  ]);

  return [...relatedEntities, ...parentEntities]
    .filter((entity): entity is Entity => Boolean(entity))
    .filter(isParentEntity)
    .sort((first, second) =>
      stringifyEntityRef(first).localeCompare(stringifyEntityRef(second)),
    )[0];
}

/**
 * Resolves the members of a participant group.
 *
 * Membership is the sole source of truth: GitHub Issue assignees never add
 * rows to onboarding progress views.
 */
export async function resolveParticipantGroupMembers(options: {
  catalogApi: CatalogApi;
  groupRefs: string[];
}): Promise<OnboardingMember[]> {
  const { catalogApi, groupRefs } = options;
  if (groupRefs.length === 0) {
    return [];
  }

  const { items: groupEntities } = await catalogApi.getEntitiesByRefs({
    entityRefs: groupRefs,
  });

  const memberRefs = new Set<string>();
  const groupRefsByMemberRef = new Map<string, Set<string>>();
  const addMemberToGroup = (memberRef: string, groupRef: string) => {
    memberRefs.add(memberRef);
    const groups = groupRefsByMemberRef.get(memberRef) ?? new Set<string>();
    groups.add(groupRef);
    groupRefsByMemberRef.set(memberRef, groups);
  };

  for (const groupEntity of groupEntities) {
    if (!groupEntity) {
      continue;
    }
    const groupRef = stringifyEntityRef(groupEntity).toLowerCase();
    for (const memberRef of getRelationTargetRefs(
      groupEntity,
      RELATION_HAS_MEMBER,
      { kind: 'user' },
    )) {
      addMemberToGroup(memberRef, groupRef);
    }
  }

  const [relatedMembers, queriedMemberResponses] = await Promise.all([
    memberRefs.size > 0
      ? catalogApi
          .getEntitiesByRefs({ entityRefs: [...memberRefs] })
          .then(response => response.items)
      : Promise.resolve([]),
    Promise.all(
      groupRefs.map(async groupRef => {
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'User',
            [`relations.${RELATION_MEMBER_OF}`]: groupRef,
          },
        });
        for (const member of response.items) {
          addMemberToGroup(stringifyEntityRef(member).toLowerCase(), groupRef);
        }
        return response.items;
      }),
    ),
  ]);

  const memberEntities = [...relatedMembers, ...queriedMemberResponses.flat()]
    .filter((member): member is Entity => Boolean(member))
    .filter(
      (member, index, allMembers) =>
        allMembers.findIndex(
          candidate =>
            stringifyEntityRef(candidate).toLowerCase() ===
            stringifyEntityRef(member).toLowerCase(),
        ) === index,
    );

  const teamsByRef = new Map<string, OnboardingTeam>();
  for (const teamEntity of groupEntities) {
    if (!teamEntity) {
      continue;
    }
    const team = toOnboardingTeam(teamEntity);
    if (team) {
      teamsByRef.set(team.entityRef, team);
    }
  }

  const members = memberEntities.map(member =>
    toOnboardingMember(
      member,
      teamsByRef,
      groupRefsByMemberRef.get(stringifyEntityRef(member).toLowerCase()) ??
        new Set(),
    ),
  );

  return dedupeByEntityRef(members).sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );
}
